/**
 * AI CONTEXT
 * Purpose: full evaluation editing modal with tabs (standout, previous eval, comments) + form actions.
 * Handles: draft save, submit, approve/reject/print, notification side effects.
 * Depends on: EvaluationFormContext + Firebase writes + analytics.
 * Used by: EvaluationView and debug dashboards.
 * Refactor: split action handlers (submit/admin actions) into separate hooks/services.
 */
import { App, Button, Col, Form, Spin, Tabs } from "antd"
import { isEmpty, mapValues, noop, pickBy } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { evaluationAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import CopyToClipboard from "#blocks/CopyToClipboard"
import Flex from "#blocks/Flex"
import Modal from "#blocks/Modal"
import { Evaluation } from "#classes/Evaluation"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { Student } from "#classes/Student"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import FormWithValues from "#components/FormWithValues"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { EVAL_EVENT_TYPE } from "#customTypes/composedTypes/evalCommentTypes"
import {
  EVALUATION_STATUS,
  EvaluationConstructor,
} from "#customTypes/composedTypes/evaluationTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  NOTIFICATION_PRIORITY,
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
} from "#customTypes/composedTypes/notificationTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useCreateNotification } from "#hooks/jotai/useCreateNotification"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import useHasPermission, {
  useCanSeeIds,
  useResourcePermissions,
} from "#hooks/newHooks/useHasPermission"
import useAnalytics from "#hooks/useAnalytics"
import {
  getEvalNotificationRecipient,
  getEventLabel,
} from "#utils/evalCommentHelpers"
import { cleanFormData } from "#utils/formHelpers"
import { ErrorType, formatError } from "#utils/textFormatters"

import EvalComments from "./EvalComments"
import EvaluationForm from "./EvaluationForm"
import EvaluationStatusTag from "./EvaluationStatusTag"
import PreviousEvalForm from "./PreviousEvalForm"
import StandoutMomentsPanel from "./StandoutMoments/StandoutMomentsPanel"
import useEvaluationFormContext from "./useEvaluationFormContext"

interface EvaluationModalProps {
  onClose: () => void
  evaluationId: Uuid
  defaultTab?: string
}

const EvaluationFormModal = ({
  onClose,
  evaluationId,
  defaultTab,
}: EvaluationModalProps) => {
  const [isPending, setIsPending] = useState({
    isPrinting: false,
    isSubmitting: false,
    isSavingDraft: false,
    isApproving: false,
    isRejecting: false,
  })

  const { canRead, canUpdate } = useResourcePermissions(RESOURCES.EVALUATION)
  const hasApprovePermission = useHasPermission(
    RESOURCES.EVALUATION,
    USER_ACTIONS.ASSIGN_TEACHER_TO_EVAL
  )

  const [isOpen, setIsOpen] = useState(true)
  // We want different statuses for submit vs save draft. Note: form.submit() is not async
  const isSubmittingRef = useRef(false)
  const isDraftModeRef = useRef(false)
  const [shouldShake, setShouldShake] = useState(false)
  const evaluation = useOneDoc<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    (canRead && evaluationId) || NULL_ID
  )

  const [form] = Form.useForm<EvaluationConstructor>()

  const student = useGetOneStudent(evaluation?.studentId)

  const { usersById, traitsById, onPrint, onApprove, onReject } =
    useEvaluationFormContext((state) => ({
      usersById: state.usersById,
      traitsById: state.traitsById,
      onPrint: state.onPrint,
      onApprove: state.onApprove,
      onReject: state.onReject,
    }))

  const evaluationForDisplay = useMemo(() => {
    if (!evaluation) return null
    return new Evaluation(evaluation).getEvaluationForDisplay(
      usersById,
      traitsById
    )
  }, [evaluation, usersById, traitsById])

  // Auto-fill checkboxes if they were cleared (e.g. by a skill level change)
  useEffect(() => {
    if (!evaluation || !canUpdate) return
    if (evaluation.status === EVALUATION_STATUS.NO_EVAL) return
    if (!isEmpty(evaluation.checkboxes)) return

    const currentSkillBase = evaluation.currentSkill?.replace("^", "")
    if (!currentSkillBase) return

    const matchingTraits = pickBy(traitsById, (trait) =>
      trait.skillLevels.includes(currentSkillBase)
    )

    if (isEmpty(matchingTraits)) return

    const restoredCheckboxes = mapValues(matchingTraits, () => false)

    void Firebase.getInstance().updateDocument(
      DOC_TYPES.EVALUATIONS,
      evaluation.id,
      { checkboxes: restoredCheckboxes }
    )
  }, [evaluation, traitsById, canUpdate])

  const { currentUserId } = useUserContext()
  const isAssignedApprover =
    !!currentUserId && currentUserId === evaluation?.approverId
  const canApprove = hasApprovePermission || isAssignedApprover
  const { message, modal } = App.useApp()
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics()
  const { createNotification } = useCreateNotification()

  const notifyRecipient = useCallback(
    (
      subtype: NOTIFICATION_SUBTYPE,
      eventLabel: string,
      priority?: NOTIFICATION_PRIORITY
    ) => {
      if (!currentUserId) return
      const recipientId = getEvalNotificationRecipient(
        currentUserId,
        evaluation?.assignedTo,
        evaluation?.approverId
      )
      if (!recipientId) return
      void createNotification({
        recipientId,
        type: NOTIFICATION_TYPE.EVALUATION,
        subtype,
        priority,
        header: `Evaluation ${eventLabel.toLowerCase()}`,
        message: eventLabel,
        metadata: { evaluationId },
      })
    },
    [
      currentUserId,
      evaluation?.assignedTo,
      evaluation?.approverId,
      evaluationId,
      createNotification,
    ]
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
    // Leave time for the animation to finish
    setTimeout(() => {
      onClose?.()
    }, 500)
  }, [onClose])

  // Runs validation
  const handleSubmitForApproval = async () => {
    if (!canUpdate) {
      throw new Error("You do not have permission to update evaluations")
    }

    if (!currentUserId) {
      modal.error({
        title: "Error",
        content: "Please login to submit an evaluation.",
      })
      return
    }

    try {
      // Validate form before submitting
      await form.validateFields()

      const now = Date.now()
      setIsPending((prev) => ({ ...prev, isSubmitting: true }))

      isSubmittingRef.current = true
      form.setFieldsValue({
        status: EVALUATION_STATUS.SUBMITTED,
        submittedBy: currentUserId,
        submittedAt: new Date().toISOString(),
        lastUpdatedBy: currentUserId,
        lastUpdatedAt: new Date().toISOString(),
      })

      await form.submit()
      notifyRecipient(
        NOTIFICATION_SUBTYPE.EVAL_SUBMITTED,
        getEventLabel(EVAL_EVENT_TYPE.SUBMITTED)
      )
      trackEvent(ANALYTICS_EVENTS.EVALUATION_COMPLETED, {
        student_id: evaluation?.studentId || "",
        class_id: evaluation?.classId || "",
      })
      const diff = Date.now() - now
      setTimeout(
        () => {
          setIsPending((prev) => ({ ...prev, isSubmitting: false }))
          onClose()
        },
        Math.max(0, 1000 - diff)
      )
    } catch {
      // Form validation failed
      setShouldShake(true)
      setIsPending((prev) => ({ ...prev, isSubmitting: false }))
      message.error("Please fill in all required fields before submitting.")
      // Reset shake state after animation completes
      setTimeout(() => setShouldShake(false), 400)
    }
  }

  // Called for saving drafts and on submit. Form validation is in handleSubmitForApproval.
  const handleFinish = async (values: EvaluationConstructor) => {
    if (!canUpdate) {
      throw new Error("You do not have permission to update evaluations")
    }

    const isSubmitting = isSubmittingRef.current
    isSubmittingRef.current = false

    const cleanedValues = cleanFormData(values) as EvaluationConstructor

    const submitData = {
      ...cleanedValues,
      status: isSubmitting
        ? EVALUATION_STATUS.SUBMITTED
        : EVALUATION_STATUS.IN_PROGRESS,
      savedAt: new Date().toISOString(),
      savedBy: currentUserId,
    }

    try {
      await Firebase.getInstance().updateEvaluationWithEvent({
        evaluationId,
        patch: submitData,
        event: currentUserId
          ? {
              authorId: currentUserId,
              eventType: isSubmitting
                ? EVAL_EVENT_TYPE.SUBMITTED
                : EVAL_EVENT_TYPE.SAVED_DRAFT,
            }
          : undefined,
      })
      void message.success("Evaluation saved successfully.")
    } catch (error) {
      void message.error(
        `Failed to save evaluation: ${formatError(error as ErrorType)}`
      )
    }
  }

  const theme = useThemeContext()

  const canSeeId = useCanSeeIds()

  const title = student ? (
    <Flex style={{ justifyContent: "space-between", marginRight: 30 }}>
      <Flex style={{ alignItems: "center" }} gap={8}>
        Evaluation for
        <ClickableStudentInfoRow student={student} border />
        {evaluationForDisplay?.semester} - {evaluationForDisplay?.classGroup}
        <EvaluationStatusTag status={evaluation?.status ?? ""} />
      </Flex>
      {canSeeId && <CopyToClipboard data={evaluationId} label={evaluationId} />}
    </Flex>
  ) : (
    "Evaluation"
  )

  const handleCloseAttempt = useCallback(() => {
    if (form.isFieldsTouched()) {
      modal.confirm({
        title: "Unsaved Changes",
        content: "Are you sure you want to close without saving?",
        okText: "Yes, close",
        cancelText: "No",
        onOk: handleClose,
        onCancel: noop,
        autoFocusButton: "ok",
      })
      return
    }
    handleClose()
  }, [handleClose, modal, form])

  const adminFooter = [
    <Button
      key="print"
      onClick={async () => {
        setIsPending((prev) => ({ ...prev, isPrinting: true }))
        if (!evaluation) return
        await onPrint(evaluationId, evaluation, traitsById)
        notifyRecipient(
          NOTIFICATION_SUBTYPE.EVAL_PRINTED,
          getEventLabel(EVAL_EVENT_TYPE.PRINTED)
        )
        setIsPending((prev) => ({ ...prev, isPrinting: false }))
      }}
      type="primary"
      style={{ backgroundColor: theme.eric.blue, color: theme.white }}
      loading={isPending.isPrinting}
    >
      Print
    </Button>,
    <Button
      key="approve"
      onClick={async () => {
        const now = Date.now()
        try {
          await form.validateFields()
          setIsPending((prev) => ({ ...prev, isApproving: true }))
          await onApprove(evaluationId)
          notifyRecipient(
            NOTIFICATION_SUBTYPE.EVAL_APPROVED,
            getEventLabel(EVAL_EVENT_TYPE.APPROVED)
          )

          // Wait at least 1 second before resetting the state so it doesn't jitter
          const diff = Date.now() - now
          setTimeout(
            () => {
              setIsPending((prev) => ({ ...prev, isApproving: false }))
            },
            Math.max(0, 1000 - diff)
          )
        } catch {
          setShouldShake(true)
          message.error("Can't approve without filling in all required fields.")
          // Reset shake state after animation completes
          setTimeout(() => {
            setShouldShake(false)
          }, 400)
        }
      }}
      style={{ backgroundColor: theme.eric.green, color: theme.white }}
      loading={isPending.isApproving}
    >
      Approve
    </Button>,
    <Button
      key="reject"
      onClick={async () => {
        const now = Date.now()
        setIsPending((prev) => ({ ...prev, isRejecting: true }))
        await onReject(evaluationId)
        notifyRecipient(
          NOTIFICATION_SUBTYPE.EVAL_REJECTED,
          getEventLabel(EVAL_EVENT_TYPE.REJECTED),
          NOTIFICATION_PRIORITY.HIGH
        )

        // Wait at least 1 second before resetting the state so it doesn't jitter
        const diff = Date.now() - now
        setTimeout(
          () => {
            setIsPending((prev) => ({ ...prev, isRejecting: false }))
          },
          Math.max(0, 1000 - diff)
        )
      }}
      style={{
        backgroundColor: theme.eric.red,
        color: theme.white,
        marginRight: 30,
      }}
      loading={isPending.isRejecting}
    >
      Reject
    </Button>,
  ]

  const footer = [
    ...(canApprove ? adminFooter : []),
    <Button key="cancel" onClick={handleCloseAttempt}>
      Cancel
    </Button>,
    <Button
      key="save"
      onClick={async () => {
        const now = Date.now()
        setIsPending((prev) => ({ ...prev, isSavingDraft: true }))
        isSubmittingRef.current = false
        isDraftModeRef.current = true
        try {
          await form.submit()
          trackEvent(ANALYTICS_EVENTS.EVALUATION_SAVED, {
            student_id: evaluation?.studentId || "",
            class_id: evaluation?.classId || "",
          })
        } catch {
          // For draft saves, we still want to save even if validation fails
          // The form will still submit with current values
        } finally {
          const diff = Date.now() - now
          setTimeout(
            () => {
              isDraftModeRef.current = false
              setIsPending((prev) => ({ ...prev, isSavingDraft: false }))
            },
            Math.max(0, 1000 - diff)
          )
        }
      }}
      type="primary"
      style={{ backgroundColor: theme.eric.gray, color: theme.white }}
      loading={isPending.isSavingDraft}
    >
      Save Draft
    </Button>,
    <Button
      key="submit"
      onClick={handleSubmitForApproval}
      type="primary"
      loading={isPending.isSubmitting}
    >
      Submit
    </Button>,
  ]

  const studentAge = student ? new Student(student).getAge() : 0

  const handleStandoutFeedback = useCallback(
    (feedback: {
      didWell?: string
      needsWork?: string
      secretMessage?: string
    }) => {
      form.setFieldsValue(feedback)
    },
    [form]
  )

  const checkboxes = Form.useWatch("checkboxes", form) ?? evaluation?.checkboxes

  const { checkedTraits, uncheckedTraits } = useMemo(() => {
    const checked: string[] = []
    const unchecked: string[] = []
    if (checkboxes) {
      for (const [traitId, isChecked] of Object.entries(checkboxes)) {
        const name = traitsById[traitId]?.name
        if (!name) continue
        if (isChecked) checked.push(name)
        else unchecked.push(name)
      }
    }
    return { checkedTraits: checked, uncheckedTraits: unchecked }
  }, [checkboxes, traitsById])

  const leftPanelTabs = useMemo(
    () => [
      {
        key: "standoutMoments",
        label: "Standout Moments",
        children: evaluation ? (
          <StandoutMomentsPanel
            studentId={evaluation.studentId}
            studentFirstName={student?.firstName ?? ""}
            semester={evaluation.semester}
            curriculumId={evaluation.curriculumId}
            age={studentAge}
            classGroup={evaluation.classGroup}
            evaluationId={evaluation.id}
            classId={evaluation.classId}
            checkedTraits={checkedTraits}
            uncheckedTraits={uncheckedTraits}
            standoutSummary={evaluation.standoutSummary}
            onFeedbackGenerated={handleStandoutFeedback}
          />
        ) : null,
      },
      {
        key: "previousEval",
        label: "Previous Eval",
        children: evaluation ? (
          <PreviousEvalForm currentEval={evaluation} />
        ) : null,
      },
      {
        key: "comments",
        label: "Comments",
        children: evaluation ? <EvalComments evaluation={evaluation} /> : null,
      },
    ],
    [
      evaluation,
      student,
      studentAge,
      checkedTraits,
      uncheckedTraits,
      handleStandoutFeedback,
    ]
  )

  if (!evaluation) {
    return (
      <Modal
        title="Evaluation"
        width={2000}
        open
        onClose={onClose}
        footer={null}
      >
        <Flex center style={{ width: "100%", height: 300 }}>
          <Spin size="large" />
        </Flex>
      </Modal>
    )
  }

  return (
    <Modal
      title={title}
      width={2000}
      open={isOpen}
      onClose={handleCloseAttempt}
      footer={footer}
    >
      <Flex col gap={30} style={{ marginTop: 0 }}>
        <Flex>
          <Col span={12}>
            <Tabs
              defaultActiveKey={defaultTab ?? "standoutMoments"}
              items={leftPanelTabs}
              size="small"
              style={{ paddingRight: 16 }}
            />
          </Col>
          <Col span={12}>
            <FormWithValues
              form={form}
              onFinish={handleFinish}
              name="evaluation"
              layout="vertical"
              initialValues={evaluation}
            >
              <AnimateInFlex
                animation={shouldShake ? ANIMATION_BY_KEY.shake : undefined}
                animationKey={"eval-form-shake-on-fail-validation"}
              >
                <EvaluationForm
                  initialValues={evaluation}
                  isDraft={() => isDraftModeRef.current}
                />
              </AnimateInFlex>
            </FormWithValues>
          </Col>
        </Flex>
      </Flex>
    </Modal>
  )
}

export default EvaluationFormModal
